// @story HIR-HR21
/* An interview report scored on the stage's approved brief (design
 * "Interview briefs & guardrails", 30 Oct 2026, §A step 7 and §D): each
 * rubric line with its level 1–5, the one-line reason and the candidate's
 * own words; the weighted overall; and, set apart, any protected topic the
 * interviewer raised. A report without `criterionScores` (a stage with no
 * brief) shows none of this and keeps its axis scores.
 *
 * The level words match the brief editor: strong (5), adequate (3), weak (1).
 */
import React from 'react';
import { Card, StatusPill } from '../../ds';

const LEVEL_WORDS = { 5: 'Strong', 4: 'Between adequate and strong', 3: 'Adequate', 2: 'Between weak and adequate', 1: 'Weak' };
const LEVEL_TONE = { 5: 'success', 4: 'success', 3: 'warning', 2: 'danger', 1: 'danger' };

const small = { fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)', lineHeight: 'var(--lh-body)' };
const muted = { color: 'var(--text-muted)', fontSize: 'var(--fs-caption)' };

/* Whether a report was scored on a brief. */
export const hasBriefScores = (report) => Array.isArray(report?.criterionScores) && report.criterionScores.length > 0;

export function BriefRubricScores({ report }) {
  if (!hasBriefScores(report)) return null;
  const v = report.brief?.version;
  return (
    <section aria-label="Scored on the interview brief" style={{ display: 'grid', gap: 12 }}>
      <Card padding="16px">
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <h3 style={{ fontSize: 'var(--fs-body-lg)', fontWeight: 600, margin: 0 }}>Scored on the interview brief</h3>
          <div className="num" style={{ fontWeight: 600, color: 'var(--text-1)' }}>
            Weighted overall: {report.overallScore ?? '—'} / 100
          </div>
        </div>
        <p style={{ ...small, margin: '6px 0 0' }}>
          {v ? `Each line of brief version ${v}, ` : 'Each line of the brief, '}
          scored 1 to 5 with the reason and the candidate's own words. The overall is the weighted average of the lines.
        </p>
      </Card>
      {report.criterionScores.map((c) => (
        <Card key={c.criterionId || c.name} padding="16px">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, color: 'var(--text-1)', flex: 1 }}>{c.name}</span>
            <span style={muted}>Weight {c.weight ?? '—'}</span>
            {c.level ? (
              <StatusPill tone={LEVEL_TONE[c.level] || 'neutral'}>{`Level ${c.level} of 5 · ${LEVEL_WORDS[c.level] || ''}`}</StatusPill>
            ) : (
              <StatusPill tone="neutral">Not scored</StatusPill>
            )}
          </div>
          {c.level ? (
            <>
              {c.reason && <p style={{ ...small, margin: '8px 0 0' }}>{c.reason}</p>}
              {c.quote && (
                <blockquote style={{ margin: '8px 0 0', padding: '6px 10px', borderLeft: '3px solid var(--border)', ...small }}>
                  “{c.quote}”
                  {c.quoteFound === false && (
                    <span style={{ ...muted, display: 'block', marginTop: 4 }}>Not found word for word in the transcript: check it there.</span>
                  )}
                </blockquote>
              )}
            </>
          ) : (
            <p style={{ ...small, margin: '8px 0 0' }}>The interview did not give enough to score this line. It is left out of the overall, never guessed.</p>
          )}
        </Card>
      ))}
    </section>
  );
}

/* A protected topic (age, gender, religion, caste, marital status,
   pregnancy, disability, health, family plans) the INTERVIEWER raised. */
export function GuardrailFlags({ flags }) {
  if (!Array.isArray(flags) || !flags.length) return null;
  const topics = [...new Set(flags.map((f) => f.topic))];
  return (
    <div role="alert" style={{ display: 'grid', gap: 8, padding: 12, borderRadius: 'var(--radius-md)', background: 'var(--status-danger-bg)', color: 'var(--status-danger-text)' }}>
      <strong>The AI interviewer asked about a protected topic: {topics.join(', ')}.</strong>
      <span style={{ fontSize: 'var(--fs-body-sm)' }}>
        It should never have been asked. It does not count against the candidate, and nothing they said about it should be used in the decision. Use "Something wasn't right" to tell EquaOS.
      </span>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 'var(--fs-body-sm)' }}>
        {flags.map((f, i) => (
          <li key={i}>{f.turn ? `Turn ${f.turn}: ` : ''}“{f.text}”</li>
        ))}
      </ul>
    </div>
  );
}

/* The same, as print HTML for the candidate's full report
   (ResumesTable). Every value is escaped: quotes are the candidate's words. */
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));

export function briefScoresHtml(report) {
  const flags = Array.isArray(report?.guardrailFlags) ? report.guardrailFlags : [];
  let out = '';
  if (flags.length) {
    out += `<div style="margin-bottom:12px;padding:10px 12px;border-radius:8px;background:#fdecee;color:#9b1c1c;font-size:12px">`
      + `<strong>The AI interviewer asked about a protected topic: ${esc([...new Set(flags.map((f) => f.topic))].join(', '))}.</strong> It does not count against the candidate.`
      + flags.map((f) => `<div style="margin-top:4px">${f.turn ? `Turn ${esc(f.turn)}: ` : ''}“${esc(f.text)}”</div>`).join('')
      + `</div>`;
  }
  if (!hasBriefScores(report)) return out;
  out += `<div style="margin-bottom:12px">`
    + `<div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#b5b1b2;margin-bottom:8px">Scored on the interview brief${report.brief?.version ? ` (version ${esc(report.brief.version)})` : ''} · weighted overall ${esc(report.overallScore ?? '—')}/100</div>`
    + report.criterionScores.map((c) => `<div style="margin-bottom:8px;font-size:12px">`
      + `<div style="display:flex;justify-content:space-between"><span style="font-weight:600">${esc(c.name)} <span style="color:#b5b1b2;font-weight:400">· weight ${esc(c.weight ?? '—')}</span></span>`
      + `<strong>${c.level ? `Level ${esc(c.level)} of 5` : 'Not scored'}</strong></div>`
      + (c.reason ? `<div style="color:#667683;margin-top:2px">${esc(c.reason)}</div>` : '')
      + (c.quote ? `<div style="margin-top:2px;font-style:italic">“${esc(c.quote)}”</div>` : '')
      + `</div>`).join('')
    + `</div>`;
  return out;
}
